import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';
import publicAxios from '../../api/publicAxios';

// Login Action
export const loginThunk = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await publicAxios.post('/auth/login/', credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { non_field_errors: ['Login failed.'] }
      );
    }
  }
);

// OTP Verification Action (for email and phone)
export const verifyOtpThunk = createAsyncThunk(
  'auth/verifyOtp',
  async ({ identifier, otp }, { rejectWithValue }) => {
    try {
      const response = await publicAxios.post('/auth/otp-verify/', {
        identifier,
        otp,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { non_field_errors: ['Invalid OTP.'] }
      );
    }
  }
);

// Generate OTP Action
export const generateOtpThunk = createAsyncThunk(
  'auth/generateOtp',
  async (identifier, { rejectWithValue }) => {
    try {
      const response = await publicAxios.post('/auth/otp-generate/', {
        identifier,
      });
      return response.data;
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 404 && error.response?.data?.error === 'User not found.') {
        return rejectWithValue({
          type: 'USER_NOT_FOUND',
          identifier,
          message: 'No account found with this email/phone number.'
        });
      }
      
      return rejectWithValue(
        error.response?.data || {
          non_field_errors: ['Failed to generate OTP.'],
        }
      );
    }
  }
);

// Fetch User Profile
export const fetchProfileThunk = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/auth/profile/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch profile');
    }
  }
);

// Sign Up Action
export const signUpThunk = createAsyncThunk(
  'auth/signUp',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await publicAxios.post('/auth/signup/', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { non_field_errors: ['Sign-up failed.'] }
      );
    }
  }
);

// Helper function to safely get user from localStorage
const getUserFromStorage = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    console.warn('Failed to parse user from localStorage:', error);
    // Don't clear localStorage on parse error - might be recoverable
    return null;
  }
};

const initialUser = getUserFromStorage();

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,
    accessToken: localStorage.getItem('accessToken') || null,
    refreshToken: localStorage.getItem('refreshToken') || null,
    isVerified:
      initialUser?.is_verified ||
      initialUser?.email_verified ||
      initialUser?.phone_verified ||
      false,
    emailVerified: initialUser?.email_verified || false,
    phoneVerified: initialUser?.phone_verified || false,
    loading: false,
    errorMessage: null,
    successMessage: null,
  },
  reducers: {
    updateTokens: (state, action) => {
      const { user, accessToken, refreshToken } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isVerified =
        user.is_verified || user.email_verified || user.phone_verified;
      state.emailVerified = user.email_verified || false;
      state.phoneVerified = user.phone_verified || false;

      // Update user in localStorage with verification status
      const updatedUser = {
        ...user,
        is_verified: state.isVerified,
        email_verified: state.emailVerified,
        phone_verified: state.phoneVerified,
      };

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Session management will be handled by SessionProvider
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isVerified = false;
      state.emailVerified = false;
      state.phoneVerified = false;
      state.errorMessage = null;
      state.successMessage = null;

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.errorMessage = null;
    },
    clearSuccess: (state) => {
      state.successMessage = null;
    },
    setVerified: (state, action) => {
      state.isVerified = true;
      if (state.user) {
        state.user = {
          ...state.user,
          is_verified: true,
          email_verified: true,
          phone_verified: true,
        };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
    restoreAuth: (state) => {
      // Restore auth state from localStorage (fallback if redux-persist hasn't restored yet)
      const storedUser = getUserFromStorage();
      const storedAccessToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      
      // Always restore if we have stored data, even if state already has user
      // This ensures localStorage is always the source of truth
      if (storedUser && storedAccessToken) {
        state.user = storedUser;
        state.accessToken = storedAccessToken;
        if (storedRefreshToken) {
          state.refreshToken = storedRefreshToken;
        }
        state.isVerified = storedUser?.is_verified || storedUser?.email_verified || storedUser?.phone_verified || false;
        state.emailVerified = storedUser?.email_verified || false;
        state.phoneVerified = storedUser?.phone_verified || false;
      } else if (storedAccessToken && !state.user) {
        // If we have token but no user, try to restore token at least
        state.accessToken = storedAccessToken;
        if (storedRefreshToken) {
          state.refreshToken = storedRefreshToken;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.isVerified =
          action.payload.user.is_verified ||
          action.payload.user.email_verified ||
          action.payload.user.phone_verified;
        state.emailVerified = action.payload.user.email_verified || false;
        state.phoneVerified = action.payload.user.phone_verified || false;

        // Store user with verification status
        const userWithVerification = {
          ...action.payload.user,
          is_verified: state.isVerified,
          email_verified: state.emailVerified,
          phone_verified: state.phoneVerified,
        };

        localStorage.setItem('accessToken', action.payload.access);
        localStorage.setItem('refreshToken', action.payload.refresh);
        localStorage.setItem('user', JSON.stringify(userWithVerification));

        // Session management will be handled by SessionProvider
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage =
          action.payload?.non_field_errors?.[0] || 'Login failed';
      })

      // OTP Verification
      .addCase(verifyOtpThunk.pending, (state) => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(verifyOtpThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isVerified = true;
        state.successMessage = 'OTP verified successfully.';

        // Store tokens and user data
        if (action.payload.access && action.payload.refresh) {
          state.accessToken = action.payload.access;
          state.refreshToken = action.payload.refresh;
          localStorage.setItem('accessToken', action.payload.access);
          localStorage.setItem('refreshToken', action.payload.refresh);
        }

        if (action.payload.user) {
          const updatedUser = {
            ...action.payload.user,
            is_verified: true,
            email_verified: true,
            phone_verified: true,
          };
          state.user = updatedUser;
          state.emailVerified = true;
          state.phoneVerified = true;
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }

        // Session management will be handled by SessionProvider
      })
      .addCase(verifyOtpThunk.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage =
          action.payload?.non_field_errors?.[0] || 'Invalid OTP';
      })

      // Generate OTP
      .addCase(generateOtpThunk.pending, (state) => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(generateOtpThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(generateOtpThunk.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage =
          action.payload?.non_field_errors?.[0] || 'Failed to generate OTP';
      })

      // Sign Up
      .addCase(signUpThunk.pending, (state) => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(signUpThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Sign-up successful.';
        state.user = action.payload.user;
        state.accessToken = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.isVerified =
          action.payload.user.is_verified ||
          action.payload.user.email_verified ||
          action.payload.user.phone_verified;
        state.emailVerified = action.payload.user.email_verified || false;
        state.phoneVerified = action.payload.user.phone_verified || false;

        localStorage.setItem('accessToken', action.payload.access);
        localStorage.setItem('refreshToken', action.payload.refresh);
        localStorage.setItem('user', JSON.stringify(action.payload.user));

        // Session management will be handled by SessionProvider
      })
      .addCase(signUpThunk.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage =
          action.payload?.non_field_errors?.[0] || 'Sign-up failed';
      })

      // Fetch Profile
      .addCase(fetchProfileThunk.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isVerified =
          action.payload.is_verified ||
          action.payload.email_verified ||
          action.payload.phone_verified;
        state.emailVerified = action.payload.email_verified || false;
        state.phoneVerified = action.payload.phone_verified || false;

        localStorage.setItem('user', JSON.stringify(action.payload));
      });
  },
});

export const { logout, updateTokens, clearError, clearSuccess, setVerified, restoreAuth } =
  authSlice.actions;
export default authSlice.reducer;
